export const TAG_EDITOR_DELAY = {
  DEBOUNCE_INPUT_MS: 300,
  HIGHLIGHT_ANIMATION_MS: 400,
  HIGHLIGHT_COMPLETE_MS: 1200,
  EXIT_EDIT_DELAY_MS: 100,
  CONTENT_LOAD_DELAY_MS: 200,
  FOCUS_DELAY_MS: 50,
  TAG_INSERTION_PROTECTION_MS: 500,
} as const

export const TAG_EDITOR_COLORS = {
  DEFAULT_TAG_COLOR: 'var(--ion-color-primary)',
  DEFAULT_TAG_COLOR_HASH: 'var(--ion-color-primary)',
  HIGHLIGHT_BACKGROUND_ALPHA: 0.25,
  TAG_BACKGROUND_ALPHA: 0.15,
  TAG_BORDER_OPACITY: 1,
  CARET_COLOR: '#000000',
  CARET_COLOR_CSS_VAR: 'var(--ion-color-dark, var(--ion-text-color, #000000))',
} as const

export const TAG_EDITOR_THRESHOLDS = {
  TEXT_SIMILARITY_THRESHOLD: 0.8,
  MIN_TAGS_COUNT: 1,
} as const

export const TAG_EDITOR_STRINGS = {
  EMPTY_PARAGRAPH: '<p><br></p>',
  TAG_PREFIX: 'tag_',
  TAG_TITLE_PREFIX: 'Tag: ',
  DEFAULT_URL: 'https://',
  CONTENTEDITABLE_TRUE: 'true',
  CONTENTEDITABLE_FALSE: 'false',
  TEXT_HTML_MIME_TYPE: 'text/html',
  TEXT_PLAIN_MIME_TYPE: 'text/plain',
  FIXED_POSITION: 'fixed',
  EMPTY_STRING: '',
  EMPTY_STYLE: '',
  EMPTY_STYLE_SEMICOLON: ';',
  HASH_SYMBOL: '#',
  NON_BREAKING_SPACE: '\u00A0',
  SPACE: ' ',
  IP_JOURNAL_URL_PREFIX: '/menu/app/tabs/clients/client/',
  IP_JOURNAL_URL_SUFFIX: '/ip-journal',
  CSS_IMPORTANT: 'important',
  CARET_COLOR_PROPERTY: 'caret-color',
} as const

export const DEBUG_MESSAGES = {
  ARROW_RIGHT_DETECTED: 'Arrow right detected inside tag:',
  ARROW_LEFT_DETECTED: 'Arrow left detected inside tag:',
  CURSOR_NOT_AT_END: 'Cursor not at end of tag, allowing natural movement',
  CURSOR_NOT_AT_START: 'Cursor not at start of tag, allowing natural movement',
  CURSOR_AT_END: 'Cursor at end of tag, moving outside',
  CURSOR_AT_START: 'Cursor at start of tag, moving outside',
  CURSOR_MOVED_OUTSIDE: 'Cursor moved outside tag',
  CURSOR_MOVED_BEFORE_TAG: 'Cursor moved before tag',
} as const

export const CSS_SELECTORS = {
  TAG_ANCHOR: '.tag-anchor',
  TAG_BY_ID: '[data-tag-id]',
  TAG_BY_DATA_ATTR: '[data-tag-id]',
  EDITOR_CONTENT: '.editor-content',
  TOOLBAR: '.editor-toolbar',
  TAG_SIDEBAR: '.tag-sidebar',
  CONTENTEDITABLE: '[contenteditable="true"]',
} as const

export const CSS_CLASSES = {
  TAG_ANCHOR: 'tag-anchor',
  TAG_EDITING: 'tag-editing',
  TAG_HIGHLIGHT: 'tag-highlight',
  TAG_HIGHLIGHT_PHASE2: 'tag-highlight-phase2',
  DISABLED: 'disabled',
  ACTIVE: 'active',
  TOOLBAR_BUTTON: 'toolbar-button',
} as const

export const HTML_ATTRIBUTES = {
  DATA_TAG_ID: 'data-tag-id',
  DATA_LABEL: 'data-label',
  DATA_COLOR: 'data-color',
  DATA_CATEGORY: 'data-category',
  DATA_CATEGORY_ID: 'data-category-id',
  CONTENTEDITABLE: 'contenteditable',
  SPELLCHECK: 'spellcheck',
  CLASS: 'class',
  TITLE: 'title',
  STYLE: 'style',
  ID: 'id',
} as const

export const HTML_VALUES = {
  CONTENTEDITABLE_TRUE: 'true',
  CONTENTEDITABLE_FALSE: 'false',
  SPELLCHECK_FALSE: 'false',
  TAG_TITLE_PREFIX: 'Tag: ',
} as const

export const GOOGLE_DOCS_ARTIFACTS = {
  ID_PREFIX: 'docs-internal-guid',
} as const

export const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  ESCAPE: 'Escape',
  BACKSPACE: 'Backspace',
  DELETE: 'Delete',
  SPACE: ' ',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
  PAGE_UP: 'PageUp',
  PAGE_DOWN: 'PageDown',
  CUT: 'x',
  COPY: 'c',
  PASTE: 'v',
} as const

export const TAG_EDITOR_VALUES = {
  SIMILARITY_THRESHOLD_LOW: 0.3,
  SIMILARITY_THRESHOLD_HIGH: 0.5,
  MIN_TAG_TEXT_LENGTH: 50,
  FOCUS_DELAY_MS: 50,
  EXIT_EDIT_DELAY_MS: 100,
  CONTENT_LOAD_DELAY_MS: 200,
  DEBOUNCE_INPUT_MS: 300,
  HIGHLIGHT_ANIMATION_MS: 400,
  HIGHLIGHT_COMPLETE_MS: 1200,
  PASTE_RESTORE_DELAY_MS: 50,
  DEFAULT_TIMEOUT_MS: 100,
  CONTENT_PREVIEW_LENGTH: 200,
  TEXTAREA_OFFSET_POSITION: -999999,
  TAG_TEXT_PREVIEW_LENGTH: 50,
  CURSOR_EXIT_DELAY_MS: 10,
} as const

export const CSS_STYLE_VALUES = {
  TAG_BORDER_RADIUS: '3px',
  TAG_FONT_WEIGHT: '500',
  TAG_TEXT_DECORATION: 'underline',
  TAG_TEXT_DECORATION_NONE: 'none',
  TAG_CURSOR: 'pointer',
  TAG_PADDING: '2px 4px',
  TAG_BORDER_WIDTH: '1px solid',
  SCROLL_BEHAVIOR: 'smooth',
  SCROLL_BLOCK: 'center',
  SCROLL_INLINE: 'nearest',
} as const

export const DOM_ELEMENT_NAMES = {
  SPAN: 'span',
  DIV: 'div',
  TEXTAREA: 'textarea',
  ANCHOR: 'A',
  BR: 'BR',
  LIST_ITEM: 'li',
  ORDERED_LIST: 'ol',
  UNORDERED_LIST: 'ul',
} as const

export const LIST_TYPES = {
  UNORDERED: 'ul',
  ORDERED: 'ol',
} as const

export type ListType = typeof LIST_TYPES.UNORDERED | typeof LIST_TYPES.ORDERED

/**
 * Whitelist of HTML elements that should be preserved inside tags when normalizing tag structure
 * All tag names are uppercase (matching tagName property which returns uppercase)
 * This allows formatting, lists, and structural elements to be preserved when tags are applied
 */
export const PRESERVED_FORMATTING_ELEMENTS: ReadonlySet<string> = new Set([
  // Formatting elements
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  // List elements
  'LI',
  'UL',
  'OL',
  // Structural elements
  'BR',
  'P',
  'TABLE',
  'TR',
  'TD',
  'TH',
  'THEAD',
  'TBODY',
  'TFOOT',
])

export const DEFAULT_VALUES = {
  BLACK_COLOR: '#000000',
  TAG_FALLBACK_PREFIX: 'tag_',
} as const

export const MUTATION_OBSERVER_CONFIG = {
  CHILD_LIST: true,
  CHARACTER_DATA: true,
  SUBTREE: true,
  ATTRIBUTES: false,
} as const

export const NODE_TYPES = {
  TEXT_NODE: Node.TEXT_NODE,
  ELEMENT_NODE: Node.ELEMENT_NODE,
} as const

export const CURSOR_PRESERVATION_DELAYS = {
  USER_INPUT_FLAG_CLEAR_MS: 100,
  RESTORE_DEBOUNCE_MS: 16,
} as const

export const BUTTON_TAG_NAMES = {
  ION_BUTTON: 'ION-BUTTON',
  BUTTON: 'BUTTON',
  ION_BUTTON_LOWERCASE: 'ion-button',
  BUTTON_LOWERCASE: 'button',
} as const

export const COMPONENT_STRINGS = {
  COMPONENT_NAME: 'TagEditorComponent',
  CHANGE_DETECTION_SKIP_REASON: 'TagEditorCustom: initialContent same as current, skipping load',
  CHANGE_DETECTION_LOAD_REASON: 'TagEditorCustom: initialContent changed, loading...',
} as const

export const HTML_ELEMENT_SELECTORS = {
  ION_ICON_SAVE: 'ion-icon[name="save"]',
} as const

export const DEBUG_PREVIEW_LENGTHS = {
  CONTENT_PREVIEW_SHORT: 50,
  CONTENT_PREVIEW_MEDIUM: 100,
  CONTENT_PREVIEW_LONG: 500,
  RETRY_DELAY_MS: 10,
  TEXT_MISMATCH_PREVIEW: 50,
} as const

export const NAVIGATION_KEYS: ReadonlyArray<string> = [
  KEYBOARD_KEYS.ARROW_UP,
  KEYBOARD_KEYS.ARROW_DOWN,
  KEYBOARD_KEYS.ARROW_LEFT,
  KEYBOARD_KEYS.ARROW_RIGHT,
  KEYBOARD_KEYS.HOME,
  KEYBOARD_KEYS.END,
  KEYBOARD_KEYS.PAGE_UP,
  KEYBOARD_KEYS.PAGE_DOWN,
] as const
